// Core packages
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';

// Third party packages
import { CookieService } from 'ngx-cookie-service';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from 'src/app/shared/services/config.service';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {
  private loggedUser: IUser;
  private loggedUserPermissions: string[];
  private lang: string;

  constructor(
    private router: Router,
    private authService: AuthService,
    private cookieService: CookieService,
    private configService: ConfigService,
  ) { }

  /**
   * CanActivete method
   * check if user is logged in (in our authService) and if
   * he has an allowed role.
   * Allowed roles must be passed as array in the routing module
   * with `data: { roles ['admin', 'operator', 'adc', 'cdc', 'cdc'] }
   *
   * @see https://stackoverflow.com/questions/42719445/pass-parameter-into-route-guard
   *
   * @since 1.0.0
   *
   * @returns boolean true if user is allowed, false and redirect otherwhise
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    this.loggedUser = this.authService.loggedUser$.value;
    this.loggedUserPermissions = this.authService.loggedUserPermissions$.value;
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    // Re-check is user is logged in our authService
    // If not redirect to login page
    if (!this.loggedUser || !this.loggedUser._id) {
      this.router.navigate([this.lang, 'auth', 'login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    // Get requested permissions from app-routing config
    const requestedPermissions = route.data.permissions as Array<string>;

    // Optional: route open only to some role types (e.g. ['admin', 'operator']),
    // checked against the role the user is currently acting with
    const requestedRoleTypes = route.data.roleTypes as Array<string>;

    // Check is user's role is allowed
    if (this.hasAtLeastOnePermisson(requestedPermissions) && this.hasAllowedRoleType(requestedRoleTypes)) {
      return true;
    }

    // If we came here user is not allowed.
    // Redirect to 404 page an return false
    this.router.navigate([this.lang, 'errors', '400']);
    return false;
  }

  /**
   * Check if logged user has at least one of the permissions
   * which are requested to access to this route
   *
   * @since 1.0.0
   */
  hasAtLeastOnePermisson(requestedPermissions: string[]): boolean {
    // If item does not require permission, just return true
    if (!requestedPermissions || !requestedPermissions.length) {
      return true;
    }

    // Check if loggedUser has at least one of the requested permissions
    const found = requestedPermissions.some(perm => this.loggedUserPermissions.indexOf(perm) >= 0);
    return found;
  }

  /**
   * Check if the requested role type is one of those allowed on the route.
   * Routes without `data.roleTypes` keep allowing every role type.
   */
  hasAllowedRoleType(requestedRoleTypes: string[]): boolean {
    if (!requestedRoleTypes || !requestedRoleTypes.length) {
      return true;
    }
    const roleType = this.authService.loggedUserRequestedRoleType$.value;
    return !!roleType && requestedRoleTypes.indexOf(roleType) >= 0;
  }
}
